import { applicationLocalStorage } from '@application/application_storage_local';
import { DateService } from '@application/time/date.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { useResolve } from '@visiba/instantiation';
import type { Dayjs } from 'dayjs';
import { useMemo } from 'react';

const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
	redRobinSessionId: null,
	asAgentFor: null,
	attachFiles: true,
	lockFilters: false,
	descriptionOfMatter: null,
	reasonForVisit: null,
	locationType: null,
	locationId: null,
	filterResourceId: null,
	filterSpecialityId: null,
	filterUnitId: null,
	fromDate: null,
	toDate: null,
	searchContextId: null,
	additionalParameters: null,
};

export interface ViewBookingSettingsHook {
	getSettings(): BookingSettings;
	setSettings(flowSettings: Partial<BookingSettings>): void;
	clearSettings(): void;
}

export function useBookingSettings(): ViewBookingSettingsHook {
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);
	const dateService = useResolve(DateService);
	const receptionId = useMemo(
		() => unitService.getUnitNodeInTree(universalConfig.selectedUnitId).receptionId,
		[unitService, universalConfig.selectedUnitId],
	);

	return useMemo(() => {
		const getSettings = (): BookingSettings => {
			const settings = applicationLocalStorage.get('bookingSettings') ?? {};
			const receptionSettings: Partial<BookingSettingsRaw> = settings[receptionId] ?? {};

			return {
				...DEFAULT_BOOKING_SETTINGS,
				...receptionSettings,
				fromDate: receptionSettings.fromDate ? dateService.newDate(receptionSettings.fromDate) : null,
				toDate: receptionSettings.toDate ? dateService.newDate(receptionSettings.toDate) : null,
			};
		};

		const setSettings = (flowSettings: Partial<BookingSettings>): void => {
			const settings = applicationLocalStorage.get('bookingSettings') ?? {};

			settings[receptionId] = {
				...DEFAULT_BOOKING_SETTINGS,
				...flowSettings,
				fromDate: flowSettings.fromDate?.format('YYYY-MM-DDTHH:mm:ss') ?? null,
				toDate: flowSettings.toDate?.format('YYYY-MM-DDTHH:mm:ss') ?? null,
			};

			applicationLocalStorage.set('bookingSettings', settings);
		};

		const clearSettings = (): void => {
			const settings = applicationLocalStorage.get('bookingSettings') ?? {};

			delete settings[receptionId];

			applicationLocalStorage.set('bookingSettings', settings);
		};

		return { getSettings, setSettings, clearSettings };
	}, [receptionId, dateService]);
}

interface BookingSettingsCommon {
	descriptionOfMatter: Nullable<string>;
	reasonForVisit: Nullable<number[]>;
	redRobinSessionId: Nullable<string>;
	asAgentFor: Nullable<string>;
	attachFiles: boolean;
	lockFilters: boolean;
	locationType: Nullable<string>;
	locationId: Nullable<number>;
	filterResourceId: Nullable<number>;
	filterSpecialityId: Nullable<number>;
	filterUnitId: Nullable<number>;
	searchContextId: Nullable<string>;
	additionalParameters: Record<string, string> | null;
}

export interface BookingSettingsRaw extends BookingSettingsCommon {
	fromDate: Nullable<string>;
	toDate: Nullable<string>;
}
export interface BookingSettings extends BookingSettingsCommon {
	fromDate: Nullable<Dayjs>;
	toDate: Nullable<Dayjs>;
}
